// src/features/student-management/ui/organisms/PersonForm/PersonFormActionBar.tsx
import { Button, Tag } from '@ads/components-react';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';

import type { StepSaveStatus } from './personFormHelpers';

export interface PersonFormActionBarProps {
  readonly currentStep: number;
  readonly isLastStep: boolean;
  readonly isEditMode: boolean;
  readonly isSubmitting: boolean;
  readonly stepSaveStatus: StepSaveStatus;
  readonly stepSaveError: string | null;
  readonly onCancelClick: () => void;
  readonly onPrevious: () => void;
  readonly onNext: () => Promise<void>;
  readonly onSaveRoles: () => Promise<void>;
  /** Chamado ao clicar "Salvar" no último step em modo criação. Usa type="button" para evitar submit implícito por reconciliação React. */
  readonly onSave: () => void;
}

/**
 * Barra de ações fixa do formulário de pessoa.
 *
 * Renderiza os botões de cancelar, anterior, próximo e salvar, além do
 * indicador de status de salvamento por step (loading / success / error).
 */
export function PersonFormActionBar({
  currentStep,
  isLastStep,
  isEditMode,
  isSubmitting,
  stepSaveStatus,
  stepSaveError,
  onCancelClick,
  onPrevious,
  onNext,
  onSaveRoles,
  onSave,
}: PersonFormActionBarProps) {
  const { t } = useTranslation('personManagement');

  return (
    <div className="mt-xxs flex items-center justify-between gap-xs rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
      <Button size="lg" hierarchy="secondary" type="button" onClick={onCancelClick}>
        {t('actions.cancel')}
      </Button>

      <div className="flex flex-1 justify-center">
        {stepSaveStatus === 'loading' && (
          <p className="animate-pulse ts-caption-xxs text-ctx-content-placeholder">
            {t('personForm.stepSave.saving')}
          </p>
        )}
        {stepSaveStatus === 'success' && (
          <Tag
            size="sm"
            status="positive"
            label={t('personForm.stepSave.success')}
            custom={{ text: 'text-nowrap' }}
          />
        )}
        {stepSaveStatus === 'error' && (
          <Tag
            size="sm"
            status="negative"
            label={stepSaveError ?? t('personForm.stepSave.error')}
            custom={{ text: 'text-nowrap' }}
          />
        )}
      </div>

      <div className="flex items-center gap-xs">
        <Button
          size="lg"
          hierarchy="secondary"
          type="button"
          leadingIcon={ArrowLeftIcon}
          disabled={currentStep === 0 || stepSaveStatus === 'loading'}
          onClick={onPrevious}
        >
          {t('personForm.wizard.previous')}
        </Button>

        {isLastStep ? (
          isEditMode ? (
            <Button
              size="lg"
              type="button"
              isLoading={stepSaveStatus === 'loading'}
              disabled={stepSaveStatus === 'loading'}
              onClick={() => void onSaveRoles()}
            >
              {t('actions.save')}
            </Button>
          ) : (
            <Button size="lg" type="button" isLoading={isSubmitting} onClick={onSave}>
              {t('actions.save')}
            </Button>
          )
        ) : (
          <Button
            size="lg"
            type="button"
            trailingIcon={ArrowRightIcon}
            isLoading={stepSaveStatus === 'loading'}
            disabled={stepSaveStatus === 'loading'}
            onClick={() => void onNext()}
          >
            {t('personForm.wizard.next')}
          </Button>
        )}
      </div>
    </div>
  );
}
